/**
 * The go-wallet REST client.
 *
 * Backend CLAUDE.md §4: "**External-facing communication is REST.** The
 * frontends (web, Android, iOS) talk REST to this service." This repo never
 * speaks gRPC — that is internal service-to-service only, and mTLS besides.
 *
 * Two things this client does that a generated one would not:
 *
 *   1. **It carries idempotency keys.** Invariant 5: idempotency keys are
 *      caller-supplied and binding, carried unchanged to the EMI. A retry that
 *      mints a fresh key is a second payment.
 *   2. **It surfaces `decision_ref` on a refusal.** Invariant 8: every refusal
 *      surfaces a `decision_ref` to the caller. If the UI drops it, an operator
 *      cannot reconstruct why a customer was refused six months later, which is
 *      the whole point of invariant 9.
 */

import type { AppConfig } from './config'
import type { Money, MoneyDTO } from './money'
import { moneyFromDTO } from './money'

/**
 * Listeners notified when the backend rejects us as unauthenticated.
 *
 * A module-level registry rather than a callback threaded through every call
 * site: a 401 can come back from any request on any screen, and the response is
 * always the same — re-ask Ory who we are. Passing a handler down to each
 * caller would mean every future caller has to remember to, and the one that
 * forgets is the one that silently renders an empty screen to a signed-out
 * customer.
 */
type UnauthenticatedListener = () => void
const unauthenticatedListeners = new Set<UnauthenticatedListener>()

/** Subscribe. Returns an unsubscribe function, for use from `useEffect`. */
export function onUnauthenticated(fn: UnauthenticatedListener): () => void {
  unauthenticatedListeners.add(fn)
  return () => {
    unauthenticatedListeners.delete(fn)
  }
}

function notifyUnauthenticated(): void {
  for (const fn of unauthenticatedListeners) {
    try {
      fn()
    } catch {
      // A broken listener must not turn an ordinary 401 into an unhandled
      // rejection on the money path.
    }
  }
}

/**
 * What a PERSON may be shown when a call fails.
 *
 * # Not every thrown thing is customer copy
 *
 * An `ApiError` carries the backend's own sentence, rendered from the copy
 * catalog for the customer's language — that is safe to show and is why several
 * screens render it verbatim.
 *
 * Everything else is not. A `TypeError` from a dead network, and above all THIS
 * MODULE'S OWN invariant-5 guard, are notes from us to us. One of them reached a
 * customer's screen in full:
 *
 *   "POST /v1/handle requires an idempotency key (invariant 5): it is
 *    caller-supplied and binding ... A route whose contract publishes no
 *    Idempotency-Key must pass `replayGuardedBy` naming what does the job
 *    instead."
 *
 * That is an architecture note printed under a text box, in red, to somebody
 * choosing a name. It is the same failure as showing "amount minor" to a person:
 * our vocabulary, escaping onto a screen that is not for us.
 *
 * So a screen that renders a failure renders THIS, and the developer's sentence
 * goes to the console where it belongs.
 */
export function customerMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message
  // Kept, because a developer needs it and a customer must not have it.
  console.error('unrenderable failure', err)
  return fallback
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Invariant 8: present on every refusal. Show it; never swallow it. */
    readonly decisionRef?: string,
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * The error envelope, which is not one shape but two.
 *
 * The customer surface names the machine-readable reason `error`
 * (`internal/server/cashout.go`); the admin surface names it `code`
 * (`internal/server/admin.go`). One client talks to both, so it accepts either
 * rather than silently degrading to `http_<status>` on one of them — which is
 * what it did, discarding `not_a_customer` on every new customer's first call.
 */
interface ErrorBody {
  error?: unknown
  code?: unknown
  message?: unknown
  decision_ref?: unknown
  request_id?: unknown
}

/**
 * The caller is signed in, and has no wallet.
 *
 * Registration issues an Ory session immediately, but the identity is linked to
 * a wallet only once the email is verified — so this is the normal path for
 * every new customer rather than an edge case, and it is the state the
 * onboarding journey exists to handle. A 403 and not a 401: the session is
 * valid, so this is an authorization outcome and `onUnauthenticated` correctly
 * does not fire.
 */
export const NOT_A_CUSTOMER = 'not_a_customer'

export function isNotACustomer(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403 && e.code === NOT_A_CUSTOMER
}

export interface RequestOptions {
  readonly signal?: AbortSignal
  /**
   * Required on every mutating call. Invariant 5 — supplied by the caller,
   * stable across retries of the *same* user intent.
   */
  readonly idempotencyKey?: string
  /**
   * Names what protects THIS route from a replay, for the few routes whose
   * contract defines no `Idempotency-Key` at all.
   *
   * A string rather than a boolean, and required to be non-empty, so the
   * exemption cannot be taken silently: whoever takes it has to say what does
   * the job instead, and a reviewer reads the reason at the call site rather
   * than inferring it from an absence. `PUT /v1/privacy/consents` is the first
   * — it publishes no such header, and `version` + `text_sha256` carry the
   * guarantee: a stale pair is answered `409 text_changed` with NO row written,
   * so a replay of an old decision cannot be recorded.
   *
   * This is not a way around invariant 5. Every money instruction still
   * requires the key, and passing this on a route whose contract DOES publish
   * `Idempotency-Key` would be a defect a reviewer can see.
   */
  readonly replayGuardedBy?: string

  /**
   * Names why THIS route needs no session, for the few whose contract declares
   * `auth: none`.
   *
   * A string and not a boolean, for the same reason `replayGuardedBy` is one:
   * the exemption cannot be taken silently, and a reviewer reads the reason at
   * the call site rather than inferring it from an absence.
   *
   * What it changes is narrow and it is not authorization. A 401 from a route
   * that never asked for a session is not the backend disagreeing with this
   * client about who is signed in — it is the route not being reachable, and
   * something else answering in its place. Telling the session layer to go and
   * re-ask Ory about it is wrong twice over: it is a round trip that cannot
   * help, fired on the sign-up screen, where the person's session is fine and
   * the thing that is missing is a customer row.
   *
   * `GET /v1/privacy/consent-texts` is the first (`go-wallet#910`): a person
   * reading what they are about to agree to has no account yet, because
   * agreeing is what creates one.
   */
  readonly sessionless?: string
}

export class WalletApi {
  constructor(
    private readonly config: AppConfig,
    /**
     * Injected for tests. The default is BOUND, and that is not tidiness.
     *
     * `= fetch` captures the bare function, and calling it as
     * `this.fetchImpl(...)` sets its receiver to this WalletApi instance. A
     * browser's `fetch` requires `window`, so every call threw
     * `Failed to execute 'fetch' on 'Window': Illegal invocation` — a customer
     * who had just signed up was told their onboarding state was unreadable,
     * and every money route was on the same path (#42).
     *
     * Nothing caught it because `fetch` under Node and jsdom has no receiver
     * requirement, and every test injects a plain function that has none either.
     * The seam that makes this class testable is the same seam that hid the bug.
     */
    private readonly fetchImpl: typeof fetch = fetch,
  ) {}

  private async request<T>(
    method: string,
    path: string,
    body: unknown,
    opts: RequestOptions,
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    if (method !== 'GET' && method !== 'HEAD') {
      if (opts.idempotencyKey === undefined) {
        // An exemption has to NAME its replay guard. Anything else — a boolean,
        // a default, an omission — lets a money route acquire the exemption by
        // accident, which is the failure this guard exists to prevent.
        if (
          opts.replayGuardedBy === undefined ||
          opts.replayGuardedBy.trim() === ''
        ) {
          throw new Error(
            `${method} ${path} requires an idempotency key (invariant 5): it is caller-supplied ` +
              'and binding, and a retry without one is a second instruction. A route whose ' +
              'contract publishes no Idempotency-Key must pass `replayGuardedBy` naming what ' +
              'does the job instead.',
          )
        }
      } else {
        headers['Idempotency-Key'] = opts.idempotencyKey
      }
    }

    // Detached deliberately. `this.fetchImpl(...)` would call fetch with THIS
    // WalletApi as its receiver, and a browser's fetch requires a Window — so
    // every call threw `Failed to execute 'fetch' on 'Window': Illegal
    // invocation`. A customer who had just signed up was told their onboarding
    // state was unreadable, and every money route was on the same path (#42).
    //
    // Fixing the DEFAULT would not have been enough: an injected real `fetch`
    // has the identical constraint. Detaching here fixes both, and `const f =
    // fetch; f(url)` is exactly how the platform expects it to be called.
    //
    // Nothing caught it because fetch under Node and jsdom has no receiver
    // requirement, and every test injects a plain function that has none either.
    // The seam that makes this class testable is the seam that hid the bug.
    const { fetchImpl } = this
    const res = await fetchImpl(`${this.config.apiBaseUrl}${path}`, {
      method,
      headers,
      // The session is an Ory cookie verified at the edge (backend CLAUDE.md §6);
      // this client never holds a token of its own.
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(opts.signal === undefined ? {} : { signal: opts.signal }),
    })

    if (res.status === 204) return undefined as T

    const text = await res.text()
    let parsed: unknown = undefined
    if (text !== '') {
      try {
        parsed = JSON.parse(text)
      } catch {
        if (res.ok) throw new ApiError(res.status, 'malformed_response', 'response was not JSON')
      }
    }

    if (!res.ok) {
      // The backend verifies the session at its own edge and is the authority
      // on it (backend CLAUDE.md §6). A 401 here means it disagrees with what
      // this client believes, whatever Ory last told us — so tell the session
      // layer to go and ask again rather than carrying on with a stale idea of
      // who is signed in.
      //
      // UNLESS the route declared `auth: none`. Then a 401 says nothing about
      // the session, because the route never consulted one — see `sessionless`.
      if (res.status === 401 && opts.sessionless === undefined) notifyUnauthenticated()

      const e = (parsed ?? {}) as ErrorBody
      throw new ApiError(
        res.status,
        typeof e.error === 'string'
          ? e.error
          : typeof e.code === 'string'
            ? e.code
            : `http_${res.status}`,
        typeof e.message === 'string' ? e.message : res.statusText,
        typeof e.decision_ref === 'string' ? e.decision_ref : undefined,
        typeof e.request_id === 'string' ? e.request_id : undefined,
      )
    }
    return parsed as T
  }

  get<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>('GET', path, undefined, opts)
  }

  post<T>(path: string, body: unknown, opts: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, opts)
  }

  put<T>(path: string, body: unknown, opts: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, body, opts)
  }

  /**
   * DELETE, for the surfaces whose removal is real (go-wallet#1064, #975).
   *
   * No body, and the typed result is whatever a route chooses to return —
   * `204 No Content` for the removals this app makes, which `request` already
   * renders as an empty parse rather than a failure.
   */
  del<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>('DELETE', path, undefined, opts)
  }

  // --- Typed surfaces the Overview screen consumes --------------------------
  //
  // THESE ENDPOINTS DO NOT EXIST YET. The backend's customer REST surface is
  // currently `POST /v1/cash-outs` and nothing else; the reads below describe
  // the shape those endpoints should have, not one that has been observed.
  //
  // Said plainly here because the types are indistinguishable from a real
  // contract at a call site, and `src/data/fixtures.ts` renders convincingly
  // from them. Anyone swapping a fixture for one of these calls will get a 404,
  // and should know that before they start rather than after.

  async listHoldings(opts: RequestOptions = {}): Promise<Holding[]> {
    const dto = await this.get<{ holdings: HoldingDTO[] }>('/v1/holdings', opts)
    return dto.holdings.map(holdingFromDTO)
  }

  async getSummary(opts: RequestOptions = {}): Promise<Summary> {
    const dto = await this.get<SummaryDTO>('/v1/summary', opts)
    return summaryFromDTO(dto)
  }

  async listActivity(opts: RequestOptions = {}): Promise<ActivityEntry[]> {
    const dto = await this.get<{ entries: ActivityEntryDTO[] }>('/v1/activity', opts)
    return dto.entries.map(activityFromDTO)
  }
}

// --- Domain types -----------------------------------------------------------

export interface Holding {
  readonly id: string
  /** Display symbol, e.g. €, $, ₮, ₿. */
  readonly symbol: string
  readonly name: string
  /** e.g. "Main · ·· 12234", "Platform payouts", "Tron · self-custody". */
  readonly note: string
  /**
   * The customer's main account, which the design paints in the accent colour.
   *
   * Carried explicitly rather than inferred from "the one in the display
   * currency": a customer can hold several EUR accounts, and a self-custody
   * holding valued in EUR is not a main account. Inferring it gets the Bitcoin
   * row wrong the moment its value happens to be quoted in euros.
   */
  readonly primary: boolean
  readonly balance: Money
  /**
   * The same balance expressed in the customer's display currency, when it
   * differs. `null` when the holding already is the display currency — never a
   * zero, which would read as "worth nothing".
   */
  readonly displayValue: Money | null
}

interface HoldingDTO {
  id: string
  symbol: string
  name: string
  note: string
  primary: boolean
  balance: MoneyDTO
  display_value: MoneyDTO | null
}

function holdingFromDTO(d: HoldingDTO): Holding {
  return {
    id: d.id,
    symbol: d.symbol,
    name: d.name,
    note: d.note,
    primary: d.primary === true,
    balance: moneyFromDTO(d.balance),
    displayValue: d.display_value === null ? null : moneyFromDTO(d.display_value),
  }
}

export interface Summary {
  /** Everything the customer holds, in the display currency. */
  readonly total: Money
  /** Change over the current period, signed. */
  readonly periodChange: Money
  readonly earnedThisPeriod: Money
  readonly platformCount: number
  /** Percentage change on the previous period, e.g. 18 for "18% up on June". */
  readonly earnedChangePct: number
  /**
   * Money the platforms have not released yet.
   *
   * Invariant 1: this is *not* held by us. It is an amount reported by
   * upstream platforms and never an account balance in our ledger.
   */
  readonly pendingFromPlatforms: Money
  /** Advance fee as a percentage, e.g. 1.9. `null` when not offered. */
  readonly advanceFeePct: number | null
}

interface SummaryDTO {
  total: MoneyDTO
  period_change: MoneyDTO
  earned_this_period: MoneyDTO
  platform_count: number
  earned_change_pct: number
  pending_from_platforms: MoneyDTO
  advance_fee_pct: number | null
}

function summaryFromDTO(d: SummaryDTO): Summary {
  return {
    total: moneyFromDTO(d.total),
    periodChange: moneyFromDTO(d.period_change),
    earnedThisPeriod: moneyFromDTO(d.earned_this_period),
    platformCount: d.platform_count,
    earnedChangePct: d.earned_change_pct,
    pendingFromPlatforms: moneyFromDTO(d.pending_from_platforms),
    advanceFeePct: d.advance_fee_pct,
  }
}

export type ActivityDirection = 'in' | 'out'

export interface ActivityEntry {
  readonly id: string
  readonly initials: string
  readonly name: string
  readonly note: string
  /** ISO-8601 instant. Formatted for display at the edge, never on the server. */
  readonly bookedAt: string
  readonly amount: Money
  readonly direction: ActivityDirection
}

interface ActivityEntryDTO {
  id: string
  initials: string
  name: string
  note: string
  booked_at: string
  amount: MoneyDTO
  direction: ActivityDirection
}

function activityFromDTO(d: ActivityEntryDTO): ActivityEntry {
  return {
    id: d.id,
    initials: d.initials,
    name: d.name,
    note: d.note,
    bookedAt: d.booked_at,
    amount: moneyFromDTO(d.amount),
    direction: d.direction,
  }
}
